import React, { useCallback, useMemo, useRef, useState } from 'react';
import { ExperimentContext } from './ExperimentContext';
import type { ExperimentContextType, ExperimentResolution } from '../../experiments/types';
import { CONTROL } from '../../experiments/experiments';
import type { ExperimentCode } from '../../experiments/experiments';
import { GoogleAnalyticsHelpers } from '../../googleAnalytics/GoogleAnalyticsHelpers';
import { logEvent } from '../../utils/logger-utils';

export const ExperimentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [resolutions, setResolutions] = useState<Map<string, ExperimentResolution>>(new Map());
  const [initialized, setInitialized] = useState(false);
  const exposed = useRef<Set<string>>(new Set());

  const initialize = useCallback((map: Map<string, ExperimentResolution>) => {
    setResolutions(map);
    setInitialized(true);
  }, []);

  const getResolution = useCallback(
    (code: ExperimentCode): ExperimentResolution | undefined => resolutions.get(code),
    [resolutions]
  );

  const fireExposure = useCallback(
    (code: ExperimentCode) => {
      const r = resolutions.get(code);
      if (!r) return;
      const variant = r.shownVariant ?? CONTROL;
      const key = `${code}:${variant}`;
      if (exposed.current.has(key)) return;
      exposed.current.add(key);
      try {
        GoogleAnalyticsHelpers.UpdateDataLayer({
          category: 'Experiment',
          action: 'Exposure',
          experimentCode: code,
          variantCode: variant,
        });
      } catch {
        // analytics must never break the payment flow
      }
      logEvent({
        message: 'Experiment Exposure',
        level: 'info',
        extra: {
          experimentCode: code,
          experimentId: r.experimentId,
          variantCode: variant,
          backendVariant: r.backendVariant,
          source: r.source,
          overridden: r.backendVariant !== variant,
        },
      });
    },
    [resolutions]
  );

  const value = useMemo<ExperimentContextType>(
    () => ({ getResolution, initialize, initialized, fireExposure }),
    [getResolution, initialize, initialized, fireExposure]
  );

  return <ExperimentContext.Provider value={value}>{children}</ExperimentContext.Provider>;
};
